import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { buildTheme } from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './context/ThemeContext';
import useIdleLogout from './hooks/useIdleLogout';
import { isManagerRole } from './constants/domain';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import Dashboard from './components/dashboard/Dashboard';
import TeamDashboard from './components/tasks/TeamDashboard';
import PartsTracker from './components/parts/PartsTracker';
import Tickets from './components/tickets/Tickets';
import Charts from './components/charts/Charts';
import Reports from './components/reports/Reports';
import CalendarPage from './components/calendar/CalendarPage';
import Layout from './components/common/Layout';

const IDLE_MS = Number(process.env.REACT_APP_IDLE_LOGOUT_MS) || 3 * 60 * 60 * 1000;

// Private Route Component
const PrivateRoute = ({ children }) => {
    const auth = useAuth();

    if (auth.loading) {
        return <div>Loading...</div>;
    }

    if (!auth.user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Spec: "Auto logout 3 hours." Lives inside AuthProvider so it can call logout.
const IdleGuard = ({ children }) => {
    const { logout, user } = useAuth();

    useIdleLogout(() => {
        logout();
        toast.info('You were signed out after 3 hours of inactivity.');
    }, IDLE_MS, !!user);

    return children;
};

// Wraps a page in the app shell behind auth.
const Page = ({ children }) => (
    <PrivateRoute>
        <Layout>{children}</Layout>
    </PrivateRoute>
);

// Team Dashboard is Head/Manager/Admin only -- engineers get bounced home.
const ManagerRoute = ({ children }) => {
    const auth = useAuth();

    if (auth.loading) {
        return <div>Loading...</div>;
    }
    if (!auth.user) {
        return <Navigate to="/login" replace />;
    }
    if (!isManagerRole(auth.user.role)) {
        return <Navigate to="/" replace />;
    }
    return <Layout>{children}</Layout>;
};

// Main App Content with theme
const AppContent = () => {
    const { darkMode } = useTheme();

    const theme = useMemo(() => buildTheme(darkMode), [darkMode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <Router>
                    <IdleGuard>
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/" element={<Page><Dashboard /></Page>} />
                            <Route path="/team-dashboard" element={<ManagerRoute><TeamDashboard /></ManagerRoute>} />
                            <Route path="/parts" element={<Page><PartsTracker /></Page>} />
                            <Route path="/tickets" element={<Page><Tickets /></Page>} />
                            <Route path="/calendar" element={<Page><CalendarPage /></Page>} />
                            {/* Charts / Reports are Head/Manager/Admin only -- engineers get bounced home. */}
                            <Route path="/charts" element={<ManagerRoute><Charts /></ManagerRoute>} />
                            <Route path="/reports" element={<ManagerRoute><Reports /></ManagerRoute>} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </IdleGuard>
                </Router>
            </AuthProvider>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={darkMode ? 'dark' : 'light'}
            />
        </ThemeProvider>
    );
};

function App() {
    return (
        <CustomThemeProvider>
            <AppContent />
        </CustomThemeProvider>
    );
}

export default App;
